import { Component, type ErrorInfo, type ReactNode } from 'react';

import { CrashScreen } from './CrashScreen';

import { Sentry } from '@/lib/sentry';

/**
 * Top-level error boundary. Wraps the entire app outside of the providers and
 * the router so:
 *   - Provider crashes (QueryClient, AppConfig, i18n) get caught.
 *   - Router-level crashes that escape RouteError get caught.
 *   - Any rendering throw anywhere in the tree gets caught.
 *
 * Class component because that's still the only way to author an error boundary
 * in React. Stays small - it just toggles between children and CrashScreen.
 */

interface State {
  error: unknown;
  errorInfo: ErrorInfo | null;
}

export class GlobalErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error, errorInfo: null };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    this.setState({ error, errorInfo });
    // Console first so the original stack stays in DevTools next to React's
    // componentStack. Sentry next so the issue lands in the dashboard even
    // when the user never reports it.

    console.error('[GlobalErrorBoundary]', error, errorInfo);
    Sentry.withScope((scope) => {
      scope.setTag('source', 'react-boundary');
      if (errorInfo?.componentStack) {
        scope.setExtra('componentStack', errorInfo.componentStack);
      }
      Sentry.captureException(error);
    });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleGoHome = (): void => {
    window.location.assign('/');
  };

  render(): ReactNode {
    const { error, errorInfo } = this.state;
    if (!error) return this.props.children;

    // Merge React's componentStack into the error for richer display.
    const detail: unknown = (() => {
      if (error instanceof Error && errorInfo?.componentStack) {
        const merged = new Error(error.message);
        merged.stack = `${error.stack ?? ''}\n\nComponent stack:${errorInfo.componentStack}`;
        return merged;
      }
      return error;
    })();

    return (
      <CrashScreen
        title="The app hit an unexpected snag"
        description="We logged the failure on our side. Reloading usually clears it. If it keeps happening, share the technical details below with the engineering team."
        statusCode={500}
        error={detail}
        primaryLabel="Reload page"
        onPrimary={this.handleReload}
        secondaryLabel="Go to dashboard"
        onSecondary={this.handleGoHome}
      />
    );
  }
}
